import React, { useEffect, useRef, useState } from "react";
import { AudioWaveform } from "lucide-react";

export default function LiveAudioVisualizer({
  isActive,
  className = "w-3 h-3",
}) {
  const canvasRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const sourceRef = useRef(null);
  const streamRef = useRef(null);
  const animationRef = useRef(null);
  const [hasPermission, setHasPermission] = useState(null);

  useEffect(() => {
    if (isActive) {
      startAudioCapture();
    } else {
      stopAudioCapture();
    }

    return () => {
      stopAudioCapture();
    };
  }, [isActive]);

  const startAudioCapture = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false,
        },
      });
      streamRef.current = stream;
      setHasPermission(true);

      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      const analyser = audioContext.createAnalyser();
      const source = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 64;
      analyser.smoothingTimeConstant = 0.3;

      source.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      sourceRef.current = source;
      dataArrayRef.current = new Uint8Array(analyser.frequencyBinCount);

      draw();
    } catch (error) {
      console.warn("Microphone access denied or unavailable:", error);
      setHasPermission(false);
    }
  };

  const stopAudioCapture = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
    }

    if (audioContextRef.current && audioContextRef.current.state !== "closed") {
      audioContextRef.current.close();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }

    audioContextRef.current = null;
    analyserRef.current = null;
    sourceRef.current = null;
    streamRef.current = null;
    dataArrayRef.current = null;
  };

  const draw = () => {
    if (!analyserRef.current || !dataArrayRef.current || !canvasRef.current)
      return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const analyser = analyserRef.current;
    const dataArray = dataArrayRef.current;

    analyser.getByteFrequencyData(dataArray);

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const barCount = 5;
    const barWidth = canvas.width / barCount - 1;

    ctx.fillStyle = "white";

    for (let i = 0; i < barCount; i++) {
      const startIndex = Math.floor((i * dataArray.length) / barCount);
      const endIndex = Math.floor(((i + 1) * dataArray.length) / barCount);

      let sum = 0;
      for (let j = startIndex; j < endIndex; j++) {
        sum += dataArray[j];
      }

      const average = sum / (endIndex - startIndex);
      const normalizedHeight = average / 255;
      const minHeight = 0.1;
      const barHeight = Math.max(minHeight, normalizedHeight) * canvas.height;

      const x = i * (barWidth + 1);
      const y = canvas.height - barHeight;

      ctx.fillRect(x, y, barWidth, barHeight);
    }

    animationRef.current = requestAnimationFrame(draw);
  };

  if (hasPermission === false || !navigator.mediaDevices?.getUserMedia) {
    return <AudioWaveform className={`${className} `} />;
  }

  return (
    <div className={`${className} relative`}>
      <canvas
        ref={canvasRef}
        width={40}
        height={20}
        style={{
          color: "white",
          marginLeft: "16px",
          marginRight: "4px",
          marginBottom: "10px",
        }}
      />
      {hasPermission === null && (
        <div className="audio-waveform-container">
          <AudioWaveform className="audio-waveform" />
        </div>
      )}
    </div>
  );
}
